import type { ReactNode } from "react";
import type { FieldDef, Json, RecordData } from "../core";
import { useLocale } from "../react/providers/LocaleProvider";
import { widgets } from "../widgets";

interface CellProps {
    value: Json | undefined;
    widget?: string;
    fieldDef?: FieldDef;
    /**
     * Full record + widget options — supplied by FormView read mode so render-
     * only widgets that depend on a sibling field (qualified-value widgets:
     * monetary / measure / datetime / phone) can resolve their qualifier. Omit
     * for plain scalar cells (KanbanView card render).
     */
    record?: RecordData;
    options?: Record<string, string>;
    /** XML `display_format="…"` — re-renders m2o/x2m chip labels from the
        chip's `data` payload (FormView read mode threads it from the field). */
    displayFormat?: string | null;
}

// Thin shim over the widget registry, preserved for callers that don't have a
// full record + column context (KanbanView card render, FormView read mode).
// ListView calls the registry directly to take advantage of slot expansion +
// interactive callbacks; everything else stays on Cell.
export function Cell({ value, widget, fieldDef, record, options, displayFormat }: CellProps): ReactNode {
    const { timezone } = useLocale();
    const w = widgets.resolve(widget, fieldDef?.type);
    const synthesizedColumn = {
        name: fieldDef?.name ?? "",
        label: fieldDef?.label ?? fieldDef?.name ?? "",
        widget,
        invisible: false,
        displayFormat,
        options,
    };
    if (w?.render) {
        return w.render({
            value,
            // Record context is supplied by FormView read mode (qualified-value
            // widgets read a sibling field); other callers pass an empty row so
            // render-only widgets degrade to the bare value.
            record: (record ?? {}) as RecordData,
            column: synthesizedColumn,
            field: fieldDef,
            timezone,
        });
    }
    if (value == null || value === "") return <span className="cell-muted">—</span>;
    return <span>{String(value)}</span>;
}
